/**
 * §31.2 (V131) — lógica PURA del semáforo de comandas (sin React/DOM → testeable).
 *
 * UN SOLO LUGAR de los cortes del semáforo (lo pide el prompt). El semáforo mide el
 * PENDIENTE MÁS VIEJO (fired_at, aún no listo) contra el umbral del tenant:
 *   - verde: por debajo del umbral (age < 2/3·T)
 *   - ámbar: acercándose (2/3·T ≤ age < T)
 *   - rojo:  se pasó (age ≥ T)
 * Ejemplos: T=15 → verde<10, ámbar 10–15, rojo≥15 · T=10 → <6.7 / 6.7–10 / ≥10 ·
 *           T=20 → <13.3 / 13.3–20 / ≥20.
 */
export type KitchenColor = 'green' | 'amber' | 'red';

/** Ámbar arranca a 2/3 del umbral. Rojo al pasarse. Único lugar del corte. */
export const KITCHEN_AMBER_RATIO = 2 / 3;
/** Lista blanca del umbral del tenant (min). Espejo del server (V131). */
export const KITCHEN_ALERT_WHITELIST = [10, 15, 20] as const;
export const KITCHEN_ALERT_DEFAULT = 15;

export function clampKitchenAlertMinutes(min?: number | null): number {
  return (KITCHEN_ALERT_WHITELIST as readonly number[]).includes(Number(min))
    ? Number(min) : KITCHEN_ALERT_DEFAULT;
}

/** Color del semáforo por la edad (segundos) del pendiente más viejo vs umbral (min). */
export function kitchenColor(ageSeconds: number, thresholdMinutes?: number | null): KitchenColor {
  const t = clampKitchenAlertMinutes(thresholdMinutes) * 60;
  if (ageSeconds >= t) return 'red';
  if (ageSeconds >= t * KITCHEN_AMBER_RATIO) return 'amber';
  return 'green';
}

/** Edad en segundos desde un ISO (o null → 0). `now` = Date.now(). */
export function ageSeconds(sinceIso: string | null | undefined, now: number): number {
  if (!sinceIso) return 0;
  const t = new Date(sinceIso).getTime();
  if (!Number.isFinite(t)) return 0;
  return Math.max(0, Math.floor((now - t) / 1000));
}

/** Cronómetro mm:ss. */
export function fmtElapsed(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** Clases Tailwind por color (un solo lugar, reusadas por comanda y cocina). */
export const KITCHEN_DOT: Record<KitchenColor, string> = {
  green: 'bg-emerald-500', amber: 'bg-amber-500', red: 'bg-rose-500',
};
export const KITCHEN_CARD: Record<KitchenColor, string> = {
  green: 'border-emerald-300 bg-emerald-50',
  amber: 'border-amber-300 bg-amber-50',
  red: 'border-rose-400 bg-rose-50',
};
export const KITCHEN_TEXT: Record<KitchenColor, string> = {
  green: 'text-emerald-700', amber: 'text-amber-700', red: 'text-rose-700',
};
